/** Platform defaults — India / English / IST */
export const LOCALE_DEFAULTS = {
  language: 'en',
  currency: 'INR',
  timezone: 'Asia/Kolkata',
  dateFormat: 'DD/MM/YYYY',
  country: 'IN',
};

export type LocaleLanguage = 'en' | 'hi' | 'ar' | 'fr' | 'de' | 'es' | 'pt' | 'zh' | 'ja' | string;

export interface LocaleOption {
  code: string;
  label: string;
  nativeLabel?: string;
  symbol?: string;
}

export interface TimezoneOption {
  value: string;
  label: string;
}

export interface DateFormatOption {
  value: string;
  label: string;
}

export interface CountryOption {
  code: string;
  label: string;
}

export const LANGUAGES: LocaleOption[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية' },
  { code: 'fr', label: 'French', nativeLabel: 'Français' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português' },
  { code: 'zh', label: 'Chinese', nativeLabel: '中文' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語' },
];

export const CURRENCIES: LocaleOption[] = [
  { code: 'INR', symbol: '₹', label: 'Indian Rupee (INR)' },
  { code: 'USD', symbol: '$', label: 'US Dollar (USD)' },
  { code: 'EUR', symbol: '€', label: 'Euro (EUR)' },
  { code: 'GBP', symbol: '£', label: 'British Pound (GBP)' },
  { code: 'AED', symbol: 'د.إ', label: 'UAE Dirham (AED)' },
  { code: 'SAR', symbol: '﷼', label: 'Saudi Riyal (SAR)' },
  { code: 'SGD', symbol: 'S$', label: 'Singapore Dollar (SGD)' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar (AUD)' },
  { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar (CAD)' },
  { code: 'JPY', symbol: '¥', label: 'Japanese Yen (JPY)' },
  { code: 'CNY', symbol: '¥', label: 'Chinese Yuan (CNY)' },
];

export const TIMEZONES: TimezoneOption[] = [
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Dubai', label: 'UAE (GST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Europe/London', label: 'UK (GMT/BST)' },
  { value: 'Europe/Berlin', label: 'Germany (CET)' },
  { value: 'Europe/Paris', label: 'France (CET)' },
  { value: 'America/New_York', label: 'US Eastern' },
  { value: 'America/Chicago', label: 'US Central' },
  { value: 'America/Los_Angeles', label: 'US Pacific' },
  { value: 'Australia/Sydney', label: 'Australia (Sydney)' },
  { value: 'Pacific/Auckland', label: 'New Zealand' },
  { value: 'UTC', label: 'UTC' },
];

export const DATE_FORMATS: DateFormatOption[] = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (India, UK)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
];

export const COUNTRIES: CountryOption[] = [
  { code: 'IN', label: 'India' },
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'SG', label: 'Singapore' },
  { code: 'AU', label: 'Australia' },
  { code: 'CA', label: 'Canada' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'JP', label: 'Japan' },
  { code: 'CN', label: 'China' },
];

/** Map legacy country names / aliases → ISO codes used by Regional Settings */
const COUNTRY_ALIASES: Record<string, string> = {
  india: 'IN',
  in: 'IN',
  'united states': 'US',
  usa: 'US',
  us: 'US',
  'united kingdom': 'GB',
  uk: 'GB',
  gb: 'GB',
  'united arab emirates': 'AE',
  uae: 'AE',
  ae: 'AE',
  singapore: 'SG',
  sg: 'SG',
  australia: 'AU',
  au: 'AU',
  canada: 'CA',
  ca: 'CA',
  germany: 'DE',
  de: 'DE',
  france: 'FR',
  fr: 'FR',
  japan: 'JP',
  jp: 'JP',
  china: 'CN',
  cn: 'CN',
};

export function normalizeCountryCode(value?: string | null, fallback = LOCALE_DEFAULTS.country): string {
  if (!value) return fallback;
  const raw = String(value).trim();
  if (!raw) return fallback;
  const upper = raw.toUpperCase();
  if (COUNTRIES.some((c) => c.code === upper)) return upper;
  const mapped = COUNTRY_ALIASES[raw.toLowerCase()];
  if (mapped) return mapped;
  return fallback;
}

export function countryLabel(code?: string | null): string {
  const normalized = normalizeCountryCode(code);
  return COUNTRIES.find((c) => c.code === normalized)?.label || normalized;
}

export interface LocaleSettings {
  language: string;
  currency: string;
  timezone: string;
  dateFormat: string;
  country?: string;
}

export interface LocaleConfigResponse {
  defaults: LocaleSettings;
  languages: LocaleOption[];
  currencies: LocaleOption[];
  timezones: TimezoneOption[];
  dateFormats: DateFormatOption[];
  countries: CountryOption[];
}
